// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Alert,
  AlertDescription,
  AlertTitle,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  SearchableSelect,
} from '@/components/ui';
import {
  RefreshCw,
  AlertCircle,
  ChevronRight,
  Plus,
  Trash2,
  Loader2,
  Clock,
  Database,
  Users,
  Pencil,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchOrganizations,
  fetchOrgWorkspaces,
  type OrganizationResponse,
  type WorkspaceInfo,
} from '@/subsystems/workspaces';
import { usePipelinesList } from '@/subsystems/pipelines';
import {
  listConnectors as defaultListConnectors,
  createCache as defaultCreateCache,
  updateCache as defaultUpdateCache,
  deleteCache as defaultDeleteCache,
  triggerRefresh as defaultTriggerRefresh,
  listSubscriptions,
  createSubscription,
  updateSubscription,
  deleteSubscription,
} from '../api/connectors';
import type {
  ConnectorRecord,
  CacheRecord,
  CreateCacheRequest,
  UpdateCacheRequest,
  SubscriptionRecord,
} from '../api/types/connectors';
import { SCHEDULE_OPTIONS } from '../api/types/connectors';

// API interface for test injection
export interface ConnectorsApi {
  listConnectors: () => Promise<ConnectorRecord[]>
  createCache: (connectorId: string, data: CreateCacheRequest) => Promise<CacheRecord>
  updateCache: (connectorId: string, cacheId: string, data: UpdateCacheRequest) => Promise<CacheRecord>
  deleteCache: (connectorId: string, cacheId: string) => Promise<void>
  triggerRefresh: (connectorId: string, cacheId: string, force?: boolean) => Promise<unknown>
}

interface ConnectorsManagerProps {
  connectorsApi?: ConnectorsApi
}

const POLL_INTERVAL_MS = 10_000;
const CONNECTORS_KEY = ['admin-connectors'] as const;

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function usePipelineOptions(orgId: string, wsId: string) {
  const { data: pipelines = {} } = usePipelinesList(orgId, wsId);
  return useMemo(
    () =>
      Object.values(pipelines).map(p => ({
        value: p.id,
        label: p.name ? `${p.name} (${p.id})` : p.id,
      })),
    [pipelines],
  );
}

function CacheSubscriptions({ connectorId, cacheId }: { connectorId: string; cacheId: string }) {
  const [subscriptions, setSubscriptions] = useState<SubscriptionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Add subscriber form
  const [addOpen, setAddOpen] = useState(false);
  const [newOrgId, setNewOrgId] = useState('');
  const [newWsId, setNewWsId] = useState('');
  const [newPipelineId, setNewPipelineId] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Edit pipeline dialog
  const [editTarget, setEditTarget] = useState<SubscriptionRecord | null>(null);
  const [editPipelineId, setEditPipelineId] = useState('');

  // Org/workspace dropdowns
  const { data: allOrgs = [] } = useQuery<OrganizationResponse[]>({
    queryKey: ['organizations'],
    queryFn: fetchOrganizations,
    enabled: addOpen,
  });
  const { data: allWorkspaces = [] } = useQuery<WorkspaceInfo[]>({
    queryKey: ['workspaces', newOrgId],
    queryFn: () => fetchOrgWorkspaces(newOrgId),
    enabled: addOpen && !!newOrgId,
  });

  const orgOptions = useMemo(() => allOrgs
    .filter(o => o.orgId !== '_application_')
    .map(o => ({ value: o.orgId, label: `${o.name} (${o.orgId})` })),
    [allOrgs],
  );
  const wsOptions = useMemo(() => allWorkspaces.map(w => ({
    value: w.wsId,
    label: w.name ? `${w.name} (${w.wsId})` : w.wsId,
  })), [allWorkspaces]);

  const addPipelineOptions = usePipelineOptions(newOrgId, newWsId);
  const editPipelineOptions = usePipelineOptions(editTarget?.orgId ?? '', editTarget?.wsId ?? '');

  const loadSubs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSubscriptions(connectorId, cacheId);
      setSubscriptions(data);
    } catch (err) {
      console.error('Failed to load subscriptions:', err);
    } finally {
      setLoading(false);
    }
  }, [connectorId, cacheId]);

  useEffect(() => {
    if (expanded) loadSubs();
  }, [expanded, loadSubs]);

  const handleAdd = async () => {
    if (!newOrgId || !newWsId) return;
    setIsAdding(true);
    try {
      await createSubscription(connectorId, cacheId, {
        orgId: newOrgId,
        wsId: newWsId,
        pipelineId: newPipelineId || null,
      });
      await loadSubs();
      setAddOpen(false);
      setNewOrgId('');
      setNewWsId('');
      setNewPipelineId('');
    } catch (err) {
      console.error('Failed to create subscription:', err);
    } finally {
      setIsAdding(false);
    }
  };

  const handleEditClick = (sub: SubscriptionRecord) => {
    setEditTarget(sub);
    setEditPipelineId(sub.pipelineId || '');
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    try {
      await updateSubscription(connectorId, cacheId, editTarget.orgId, editTarget.wsId, {
        pipelineId: editPipelineId || null,
      });
      await loadSubs();
      setEditTarget(null);
    } catch (err) {
      console.error('Failed to update subscription:', err);
    }
  };

  const handleDelete = async (orgId: string, wsId: string) => {
    try {
      await deleteSubscription(connectorId, cacheId, orgId, wsId);
      setSubscriptions(prev => prev.filter(s => !(s.orgId === orgId && s.wsId === wsId)));
    } catch (err) {
      console.error('Failed to delete subscription:', err);
    }
  };

  return (
    <div className="mt-2" data-testid={`connectors-subs-${connectorId}-${cacheId}`}>
      <button
        className="flex items-center gap-1 text-xs font-[550] text-slate-500 transition-colors duration-100 hover:text-slate-800"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        data-testid={`connectors-subs-toggle-${connectorId}-${cacheId}`}
      >
        <Users className="h-3 w-3" />
        <ChevronRight className={`h-3 w-3 transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`} />
        Subscribers
        {subscriptions.length > 0 && (
          <span className="ml-1 rounded-full bg-slate-100 px-1.5 py-px text-[10.5px] font-semibold text-slate-600 tabular-nums">
            {subscriptions.length}
          </span>
        )}
      </button>

      {expanded && (
        <div className="mt-2 ml-4 space-y-2">
          {loading ? (
            <span className="text-xs text-muted-foreground"><Loader2 className="h-3 w-3 inline animate-spin mr-1" />Loading...</span>
          ) : subscriptions.length === 0 ? (
            <span className="text-xs text-muted-foreground">No subscribers</span>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-xs">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-[11px] font-semibold text-slate-500">
                    <th className="py-1.5 pr-3 font-semibold">Org</th>
                    <th className="py-1.5 pr-3 font-semibold">Workspace</th>
                    <th className="py-1.5 pr-3 font-semibold">Pipeline</th>
                    <th className="py-1.5 pr-3 font-semibold">Last Synced</th>
                    <th className="py-1.5 font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map(sub => (
                    <tr key={`${sub.orgId}-${sub.wsId}`} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 pr-3 font-mono text-[11.5px] text-slate-600">{sub.orgId}</td>
                      <td className="py-2 pr-3 font-mono text-[11.5px] text-slate-600">{sub.wsId}</td>
                      <td className="py-2 pr-3 text-slate-700">{sub.pipelineId || <span className="text-slate-300">—</span>}</td>
                      <td className="py-2 pr-3 text-slate-500">{formatTimestamp(sub.lastSyncedAt)}</td>
                      <td className="py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            aria-label={`Edit subscription ${sub.orgId}/${sub.wsId}`}
                            className="rounded-md p-1 text-slate-400 transition-colors duration-100 hover:bg-slate-100 hover:text-slate-700"
                            onClick={() => handleEditClick(sub)}
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            aria-label={`Remove subscription ${sub.orgId}/${sub.wsId}`}
                            className="rounded-md p-1 text-slate-400 transition-colors duration-100 hover:bg-[#fef2f2] hover:text-[#dc2626]"
                            onClick={() => handleDelete(sub.orgId, sub.wsId)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add subscriber */}
          {!addOpen ? (
            <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => setAddOpen(true)}>
              <Plus className="h-3 w-3 mr-1" />
              Add Subscriber
            </Button>
          ) : (
            <div className="space-y-2 border rounded p-3" data-testid={`connectors-subs-add-${connectorId}-${cacheId}`}>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Organization</Label>
                  <SearchableSelect
                    options={orgOptions}
                    value={newOrgId}
                    onValueChange={v => { setNewOrgId(v); setNewWsId(''); setNewPipelineId(''); }}
                    placeholder="Select org"
                    searchPlaceholder="Search orgs..."
                    emptyMessage="No organizations"
                  />
                </div>
                <div>
                  <Label className="text-xs">Workspace</Label>
                  <SearchableSelect
                    options={wsOptions}
                    value={newWsId}
                    onValueChange={v => { setNewWsId(v); setNewPipelineId(''); }}
                    placeholder={newOrgId ? 'Select workspace' : 'Select org first'}
                    searchPlaceholder="Search workspaces..."
                    emptyMessage="No workspaces"
                    disabled={!newOrgId}
                  />
                </div>
                <div>
                  <Label className="text-xs">Pipeline (optional)</Label>
                  <SearchableSelect
                    options={addPipelineOptions}
                    value={newPipelineId}
                    onValueChange={setNewPipelineId}
                    placeholder="Select pipeline"
                    searchPlaceholder="Search pipelines..."
                    emptyMessage="No pipelines"
                    disabled={!newWsId}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="h-7 text-xs" onClick={handleAdd} disabled={!newOrgId || !newWsId || isAdding}>
                  {isAdding ? 'Adding...' : 'Add'}
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setAddOpen(false); setNewOrgId(''); setNewWsId(''); setNewPipelineId(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Edit pipeline dialog */}
          {editTarget && (
            <Dialog open={!!editTarget} onOpenChange={open => { if (!open) setEditTarget(null); }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Subscription Pipeline</DialogTitle>
                  <DialogDescription>
                    {editTarget.orgId} / {editTarget.wsId}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 py-2">
                  <Label>Pipeline</Label>
                  <SearchableSelect
                    options={editPipelineOptions}
                    value={editPipelineId}
                    onValueChange={setEditPipelineId}
                    placeholder="Select pipeline (or clear for none)"
                    searchPlaceholder="Search pipelines..."
                    emptyMessage="No pipelines"
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
                  <Button onClick={handleEditSave}>Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const chip = {
    idle: 'bg-slate-100 text-slate-600',
    queued: 'bg-slate-100 text-slate-600',
    refreshing: 'bg-[#eff6ff] text-[#2563eb]',
    error: 'bg-[#fef2f2] text-[#dc2626]',
  }[status] ?? 'bg-slate-100 text-slate-600';
  const spinning = status === 'queued' || status === 'refreshing';

  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-[650] uppercase tracking-[0.04em] ${chip}`}>
      {spinning && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
      {status}
    </span>
  );
}

export function ConnectorsManager({ connectorsApi }: ConnectorsManagerProps = {}) {
  const listConnectors = connectorsApi?.listConnectors ?? defaultListConnectors;
  const createCache = connectorsApi?.createCache ?? defaultCreateCache;
  const updateCache = connectorsApi?.updateCache ?? defaultUpdateCache;
  const deleteCache = connectorsApi?.deleteCache ?? defaultDeleteCache;
  const triggerRefresh = connectorsApi?.triggerRefresh ?? defaultTriggerRefresh;

  const queryClient = useQueryClient();

  const {
    data: connectors = [],
    isLoading,
    isFetching,
    error: queryError,
  } = useQuery<ConnectorRecord[]>({
    queryKey: CONNECTORS_KEY,
    queryFn: () => listConnectors(),
    staleTime: 5 * 60_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      const anyActive = data?.some(c => c.caches.some(
        cache => cache.status === 'queued' || cache.status === 'refreshing'));
      return anyActive ? POLL_INTERVAL_MS : false;
    },
  });

  const isRefreshing = isFetching && !isLoading;
  const [mutationError, setMutationError] = useState<string | null>(null);
  const error = mutationError ?? (queryError instanceof Error ? queryError.message : null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: CONNECTORS_KEY });

  const [expandedConnectors, setExpandedConnectors] = useState<Set<string>>(new Set());

  // Add cache dialog
  const [addCacheOpen, setAddCacheOpen] = useState(false);
  const [addCacheConnectorId, setAddCacheConnectorId] = useState('');
  const [newCacheId, setNewCacheId] = useState('');
  const [newCacheSchedule, setNewCacheSchedule] = useState('');
  const [isCreatingCache, setIsCreatingCache] = useState(false);

  // Edit schedule dialog
  const [editScheduleOpen, setEditScheduleOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<{ connectorId: string; cacheId: string; schedule: string | null } | null>(null);
  const [editScheduleValue, setEditScheduleValue] = useState('');
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);

  // Delete cache dialog
  const [deleteCacheOpen, setDeleteCacheOpen] = useState(false);
  const [deleteCacheTarget, setDeleteCacheTarget] = useState<{ connectorId: string; cacheId: string } | null>(null);
  const [isDeletingCache, setIsDeletingCache] = useState(false);

  // Force refresh dialog
  const [forceRefreshOpen, setForceRefreshOpen] = useState(false);
  const [forceRefreshTarget, setForceRefreshTarget] = useState<{ connectorId: string; cacheId: string } | null>(null);

  // Auto-expand connectors that have caches whenever data loads
  useEffect(() => {
    setExpandedConnectors(prev => {
      const next = new Set(prev);
      for (const c of connectors) {
        if (c.caches.length > 0) next.add(c.connectorId);
      }
      return next;
    });
  }, [connectors]);

  const handleRefresh = async (connectorId: string, cacheId: string) => {
    setMutationError(null);
    try {
      await triggerRefresh(connectorId, cacheId);
      await invalidate();
    } catch (err) {
      console.error('Failed to trigger refresh:', err);
      setMutationError(err instanceof Error ? err.message : 'Failed to trigger refresh');
    }
  };

  const handleForceRefreshConfirm = async () => {
    if (!forceRefreshTarget) return;
    setMutationError(null);
    try {
      await triggerRefresh(forceRefreshTarget.connectorId, forceRefreshTarget.cacheId, true);
      await invalidate();
      setForceRefreshOpen(false);
      setForceRefreshTarget(null);
    } catch (err) {
      console.error('Failed to trigger force refresh:', err);
      setMutationError(err instanceof Error ? err.message : 'Failed to trigger force refresh');
    }
  };

  const handleAddCache = async () => {
    if (!newCacheId.trim()) return;
    setIsCreatingCache(true);
    setMutationError(null);
    try {
      await createCache(addCacheConnectorId, {
        cacheId: newCacheId.trim(),
        schedule: newCacheSchedule || null,
      });
      await invalidate();
      setAddCacheOpen(false);
      setNewCacheId('');
      setNewCacheSchedule('');
    } catch (err) {
      console.error('Failed to create cache:', err);
      setMutationError(err instanceof Error ? err.message : 'Failed to create cache');
    } finally {
      setIsCreatingCache(false);
    }
  };

  const handleEditScheduleClick = (connectorId: string, cacheId: string, schedule: string | null) => {
    setEditTarget({ connectorId, cacheId, schedule });
    setEditScheduleValue(schedule ?? '');
    setEditScheduleOpen(true);
  };

  const handleSaveSchedule = async () => {
    if (!editTarget) return;
    setIsSavingSchedule(true);
    setMutationError(null);
    try {
      await updateCache(editTarget.connectorId, editTarget.cacheId, {
        schedule: editScheduleValue || null,
      });
      await invalidate();
      setEditScheduleOpen(false);
      setEditTarget(null);
    } catch (err) {
      console.error('Failed to update schedule:', err);
      setMutationError(err instanceof Error ? err.message : 'Failed to update schedule');
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const handleDeleteCacheClick = (connectorId: string, cacheId: string) => {
    setDeleteCacheTarget({ connectorId, cacheId });
    setDeleteCacheOpen(true);
  };

  const handleDeleteCacheConfirm = async () => {
    if (!deleteCacheTarget) return;
    setIsDeletingCache(true);
    setMutationError(null);
    try {
      await deleteCache(deleteCacheTarget.connectorId, deleteCacheTarget.cacheId);
      await invalidate();
      setDeleteCacheOpen(false);
      setDeleteCacheTarget(null);
    } catch (err) {
      console.error('Failed to delete cache:', err);
      setMutationError(err instanceof Error ? err.message : 'Failed to delete cache');
    } finally {
      setIsDeletingCache(false);
    }
  };

  const toggleConnector = (connectorId: string) => {
    setExpandedConnectors(prev => {
      const next = new Set(prev);
      if (next.has(connectorId)) next.delete(connectorId);
      else next.add(connectorId);
      return next;
    });
  };

  const scheduleLabel = (schedule: string | null) => {
    if (!schedule) return 'Ad-hoc';
    const opt = SCHEDULE_OPTIONS.find(o => o.value === schedule);
    return opt ? opt.label : schedule;
  };

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center gap-2 rounded-xl border border-[#e7ecf2] bg-white px-6 py-12 shadow-[0_1px_2px_rgba(2,8,23,0.04)]"
        data-testid="connectors-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        <span className="text-[13px] text-slate-500">Loading connectors…</span>
      </div>
    );
  }

  return (
    <div data-testid="connectors-manager" className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[21px] font-[650] tracking-[-0.015em]" data-testid="connectors-page-heading">
            <span data-testid="connectors-heading">System Connectors</span>
          </h1>
          <p className="mt-[5px] text-[13px] text-slate-500">
            Feeds of external documents the platform pulls in — each cache holds one feed&apos;s
            items, and subscribed workspaces receive its documents automatically.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { void invalidate(); }}
          disabled={isRefreshing}
          data-testid="connectors-refresh-all"
          className="inline-flex items-center gap-1.5 rounded-[9px] border border-slate-200 bg-white px-3.5 py-[7px] text-[12.5px] font-[550] text-slate-700 shadow-[0_1px_2px_rgba(2,8,23,0.04)] transition-colors duration-[120ms] hover:bg-slate-50 disabled:text-slate-400"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

        {/* Error */}
        {error && (
          <Alert variant="destructive" data-testid="connectors-error">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Connectors */}
        {connectors.length === 0 ? (
          <div className="rounded-xl border border-[#e7ecf2] bg-white px-6 py-12 text-center shadow-[0_1px_2px_rgba(2,8,23,0.04)]" data-testid="connectors-empty">
            <div className="text-[13.5px] font-[550] text-slate-700">No connectors configured</div>
            <div className="mt-1 text-[12.5px] text-slate-400">
              Connectors registered by the platform will appear here.
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="connectors-list">
            {connectors.map(connector => (
              <Collapsible
                key={connector.connectorId}
                open={expandedConnectors.has(connector.connectorId)}
                onOpenChange={() => toggleConnector(connector.connectorId)}
              >
                <div
                  className="overflow-hidden rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]"
                  data-testid={`connector-card-${connector.connectorId}`}
                >
                  <CollapsibleTrigger asChild>
                    <button
                      className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors duration-100 hover:bg-slate-50"
                      data-testid={`connector-toggle-${connector.connectorId}`}
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        <ChevronRight
                          className={`h-4 w-4 flex-none text-slate-400 transition-transform duration-150 ${
                            expandedConnectors.has(connector.connectorId) ? 'rotate-90' : ''
                          }`}
                        />
                        <Database className="h-4 w-4 flex-none text-slate-400" />
                        <span className="truncate text-sm font-semibold tracking-[-0.01em]">{connector.name}</span>
                        <span className="hidden rounded-md border border-slate-200 bg-slate-100 px-2 py-[3px] font-mono text-[11.5px] text-slate-600 sm:inline">
                          {connector.connectorId}
                        </span>
                      </div>
                      <span className="flex-none text-xs text-slate-400 tabular-nums">
                        {connector.caches.length} cache{connector.caches.length !== 1 ? 's' : ''}
                      </span>
                    </button>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="border-t border-[#eef2f6] px-5 pb-4" data-testid={`connector-content-${connector.connectorId}`}>
                      {connector.description && (
                        <p className="py-2.5 text-[12.5px] text-slate-500">{connector.description}</p>
                      )}

                      {/* Caches table */}
                      <div className="overflow-x-auto rounded-[10px] border border-slate-100">
                        <table className="w-full min-w-[860px] text-[12.5px]" data-testid={`connector-caches-${connector.connectorId}`}>
                          <thead>
                            <tr className="bg-[#fafbfc] text-left text-[11.5px] font-semibold text-slate-500">
                              <th className="py-2.5 pl-4 pr-3 font-semibold">Cache ID</th>
                              <th className="py-2.5 pr-3 font-semibold">Status</th>
                              <th className="py-2.5 pr-3 font-semibold">Schedule</th>
                              <th className="py-2.5 pr-3 text-right font-semibold">Items</th>
                              <th className="py-2.5 pr-3 text-right font-semibold">Last Changes</th>
                              <th className="py-2.5 pr-3 font-semibold">Last Refresh</th>
                              <th className="py-2.5 pr-4 text-right font-semibold">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {connector.caches.map(cache => (
                              <tr
                                key={cache.cacheId}
                                className="border-t border-slate-100 transition-colors duration-100 hover:bg-slate-50"
                                data-testid={`connector-cache-row-${connector.connectorId}-${cache.cacheId}`}
                              >
                                <td className="py-3 pl-4 pr-3 font-mono text-slate-800" data-testid={`connector-cache-id-${connector.connectorId}-${cache.cacheId}`}>{cache.cacheId}</td>
                                <td className="py-3 pr-3" data-testid={`connector-cache-status-${connector.connectorId}-${cache.cacheId}`}>
                                  <StatusBadge status={cache.status} />
                                  {cache.status === 'error' && cache.errorMessage && (
                                    <span className="ml-2 text-xs text-[#dc2626]">{cache.errorMessage}</span>
                                  )}
                                </td>
                                <td className="py-3 pr-3">
                                  <button
                                    className="inline-flex items-center gap-1 rounded-md border border-transparent px-1.5 py-0.5 text-xs text-slate-600 transition-colors duration-100 hover:border-slate-200 hover:bg-white hover:text-slate-900"
                                    onClick={() => handleEditScheduleClick(connector.connectorId, cache.cacheId, cache.schedule)}
                                    data-testid={`connector-cache-schedule-${connector.connectorId}-${cache.cacheId}`}
                                  >
                                    <Clock className="h-3 w-3" />
                                    {scheduleLabel(cache.schedule)}
                                  </button>
                                </td>
                                <td className="py-3 pr-3 text-right tabular-nums text-slate-800" data-testid={`connector-cache-items-${connector.connectorId}-${cache.cacheId}`}>
                                  {cache.itemCount.toLocaleString()}
                                </td>
                                <td className="py-3 pr-3 text-right tabular-nums" data-testid={`connector-cache-changes-${connector.connectorId}-${cache.cacheId}`}>
                                  {cache.itemsUpdated > 0
                                    ? <span className="text-slate-800">{cache.itemsUpdated.toLocaleString()}</span>
                                    : <span className="text-slate-300">—</span>}
                                </td>
                                <td className="py-3 pr-3 text-xs text-slate-500" data-testid={`connector-cache-last-refresh-${connector.connectorId}-${cache.cacheId}`}>
                                  {formatTimestamp(cache.lastRefreshAt)}
                                </td>
                                <td className="py-3 pr-4">
                                  <div className="flex items-center justify-end gap-1.5">
                                    <button
                                      type="button"
                                      onClick={() => handleRefresh(connector.connectorId, cache.cacheId)}
                                      disabled={cache.status === 'queued' || cache.status === 'refreshing'}
                                      data-testid={`connector-cache-refresh-${connector.connectorId}-${cache.cacheId}`}
                                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-[550] text-slate-700 transition-colors duration-100 hover:bg-slate-50 disabled:text-slate-400"
                                    >
                                      <RefreshCw className="h-3 w-3" />
                                      Refresh
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setForceRefreshTarget({ connectorId: connector.connectorId, cacheId: cache.cacheId });
                                        setForceRefreshOpen(true);
                                      }}
                                      disabled={cache.status === 'queued' || cache.status === 'refreshing'}
                                      data-testid={`connector-cache-force-${connector.connectorId}-${cache.cacheId}`}
                                      className="inline-flex items-center rounded-lg border border-[#fecaca] bg-white px-2.5 py-1 text-xs font-[550] text-[#dc2626] transition-colors duration-100 hover:bg-[#fef2f2] disabled:border-slate-200 disabled:text-slate-400"
                                    >
                                      Force
                                    </button>
                                    <button
                                      type="button"
                                      aria-label={`Delete cache ${cache.cacheId}`}
                                      onClick={() => handleDeleteCacheClick(connector.connectorId, cache.cacheId)}
                                      data-testid={`connector-cache-delete-${connector.connectorId}-${cache.cacheId}`}
                                      className="inline-flex items-center rounded-lg p-1.5 text-slate-400 transition-colors duration-100 hover:bg-[#fef2f2] hover:text-[#dc2626]"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Subscriptions per cache */}
                      {connector.caches.map(cache => (
                        <div key={`subs-${cache.cacheId}`} className="mt-3 border-t border-slate-100 pt-2.5">
                          <div className="mb-1 flex items-center gap-1.5 text-xs text-slate-500">
                            Cache
                            <span className="rounded-md border border-slate-200 bg-slate-100 px-1.5 py-px font-mono text-[11px] text-slate-600">
                              {cache.cacheId}
                            </span>
                          </div>
                          <CacheSubscriptions connectorId={connector.connectorId} cacheId={cache.cacheId} />
                        </div>
                      ))}

                      <div className="pt-3.5">
                        <button
                          type="button"
                          onClick={() => {
                            setAddCacheConnectorId(connector.connectorId);
                            setNewCacheId('');
                            setNewCacheSchedule('');
                            setAddCacheOpen(true);
                          }}
                          data-testid={`connector-add-cache-${connector.connectorId}`}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-[550] text-slate-700 shadow-[0_1px_2px_rgba(2,8,23,0.04)] transition-colors duration-100 hover:bg-slate-50"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add Cache
                        </button>
                      </div>
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
          </div>
        )}

        {/* Add Cache Dialog */}
        <Dialog open={addCacheOpen} onOpenChange={setAddCacheOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Cache</DialogTitle>
              <DialogDescription>
                Add a new cache to <span className="font-semibold">{addCacheConnectorId}</span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="cache-id">Cache ID</Label>
                <Input
                  id="cache-id"
                  placeholder="e.g., 119"
                  value={newCacheId}
                  onChange={e => setNewCacheId(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cache-schedule">Schedule</Label>
                <Select value={newCacheSchedule} onValueChange={setNewCacheSchedule}>
                  <SelectTrigger>
                    <SelectValue placeholder="Ad-hoc only" />
                  </SelectTrigger>
                  <SelectContent>
                    {SCHEDULE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value || '_adhoc'}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddCacheOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddCache}
                disabled={!newCacheId.trim() || isCreatingCache}
              >
                {isCreatingCache ? 'Creating...' : 'Add Cache'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Schedule Dialog */}
        <Dialog open={editScheduleOpen} onOpenChange={setEditScheduleOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Schedule</DialogTitle>
              <DialogDescription>
                Change the refresh schedule for{' '}
                <span className="font-semibold">
                  {editTarget?.connectorId}/{editTarget?.cacheId}
                </span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="edit-schedule">Schedule</Label>
                <Select value={editScheduleValue || '_adhoc'} onValueChange={v => setEditScheduleValue(v === '_adhoc' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Ad-hoc only" />
                  </SelectTrigger>
                  <SelectContent>
                    {SCHEDULE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value || '_adhoc'}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditScheduleOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveSchedule} disabled={isSavingSchedule}>
                {isSavingSchedule ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Cache Dialog */}
        <AlertDialog open={deleteCacheOpen} onOpenChange={setDeleteCacheOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Cache</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete cache{' '}
                <span className="font-semibold">
                  {deleteCacheTarget?.connectorId}/{deleteCacheTarget?.cacheId}
                </span>
                ? This will remove the cache definition. Existing cached data in S3 is not affected.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeletingCache}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteCacheConfirm}
                disabled={isDeletingCache}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              >
                {isDeletingCache ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Force Refresh Dialog */}
        <AlertDialog open={forceRefreshOpen} onOpenChange={setForceRefreshOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Force Refresh Cache</AlertDialogTitle>
              <AlertDialogDescription>
                This will delete the existing cached data and perform a full re-fetch for{' '}
                <span className="font-semibold">
                  {forceRefreshTarget?.connectorId}/{forceRefreshTarget?.cacheId}
                </span>
                . This may take several minutes.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleForceRefreshConfirm}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              >
                Force Refresh
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </div>
  );
}
